import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { resolveTrialBanner, TrialBanner } from './resolveTrialBanner';
import { useEmitTrackingEventCallback } from 'src/hooks/hooks';
import { useGetOrgConfigQuery } from 'src/services/settings/settingsApi';

export const TRIAL_BANNER_HEIGHT_PX = 36;
const DISMISSED_STORAGE_KEY = 'trial_banner_dismissed_expiry';
const RECOMPUTE_INTERVAL_MS = 60000;

const readDismissedExpiry = (): string | null => {
  try {
    return window.sessionStorage.getItem(DISMISSED_STORAGE_KEY);
  } catch {
    return null;
  }
};

interface UseTrialBannerResult {
  banner: TrialBanner | null;
  onDismiss: () => void;
}

/**
 * Owns everything stateful about the trial banner and returns only what should
 * actually be shown, so the layout can render the strip without knowing any of
 * the rules behind it.
 */
export const useTrialBanner = (): UseTrialBannerResult => {
  const { pathname } = useLocation();
  const emitTrackingEventCallback = useEmitTrackingEventCallback();
  const { data: orgConfig } = useGetOrgConfigQuery();

  const [now, setNow] = useState(() => Date.now());
  const [dismissedExpiry, setDismissedExpiry] = useState(readDismissedExpiry);

  // A session can outlive a threshold, so the banner escalates in place rather
  // than waiting for a reload.
  useEffect(() => {
    setNow(Date.now());
    const interval = window.setInterval(
      () => setNow(Date.now()),
      RECOMPUTE_INTERVAL_MS,
    );
    return () => window.clearInterval(interval);
  }, [pathname]);

  const resolved = useMemo(
    () => resolveTrialBanner(orgConfig, now),
    [orgConfig, now],
  );

  // Dismissal is keyed on the expiry it was made against, so an extended trial
  // resurfaces on its own instead of needing the flag cleared.
  const isDismissed =
    resolved?.isDismissable === true &&
    dismissedExpiry !== null &&
    dismissedExpiry === orgConfig?.trial_expires_at;
  const banner = resolved && !isDismissed ? resolved : null;

  const reportedTone = useRef<string | null>(null);
  useEffect(() => {
    if (!banner || reportedTone.current === banner.tone) {
      return;
    }
    reportedTone.current = banner.tone;
    emitTrackingEventCallback('trial-banner-impression', {
      trial_banner_state: banner.tone,
    });
  }, [banner, emitTrackingEventCallback]);

  const onDismiss = useCallback(() => {
    const expiry = orgConfig?.trial_expires_at ?? null;
    try {
      if (expiry !== null) {
        window.sessionStorage.setItem(DISMISSED_STORAGE_KEY, expiry);
      }
    } catch {
      // Private-browsing storage failures shouldn't keep the banner up.
    }
    setDismissedExpiry(expiry);
  }, [orgConfig?.trial_expires_at]);

  return { banner, onDismiss };
};
